/* Simulator — day-trading practice with virtual capital.
 *
 * The simulator is the terminal's flight simulator: virtual cash, simulated
 * fills with commission and slippage, and discipline rails (max daily loss,
 * max position size) from the trading business plan. Blowing up costs nothing
 * but pride — the RESET button says so and counts how often it was pressed,
 * because "I would never revenge-trade" is a claim the counter tests.
 *
 * Honesty rules this panel enforces:
 *
 *   * A permanent "SIMULATED — practice only" badge. Practice results are
 *     never merged with backtest, paper or live figures, and nothing here
 *     reaches a venue.
 *   * Quote provenance is shown. A RANDOM_WALK price is an invented number;
 *     labelling it like a market feed would train instincts on noise.
 *   * The reset counter is visible, not hidden. Resetting away a loss is a
 *     habit worth noticing.
 *
 * Permissions: the simulator rides on the backtest permissions —
 * backtest:view to look, backtest:run to act. A trainee can practice without
 * order:create, and backtest:run gives no path to a real venue.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { api, ApiError } from '@/lib/api'
import { useStore } from '@/store'
import { Panel } from '@/components/Panel'
import { Empty, Meter } from '@/components/viz'
import { fmtDateTime, fmtMoney, fmtPrice, isSimulatedSource } from '@/lib/format'
import type {
  OrderSide, SimDataSource, SimMode, SimOrder, SimOrderType, SimQuote,
  SimSession, SimSnapshot,
} from '@/types'

const SNAPSHOT_POLL_MS = 4000

export function Simulator() {
  const can = useStore((s) => s.can)
  const canView = can('backtest:view')
  const canAct = can('backtest:run')

  const [sessions, setSessions] = useState<SimSession[]>([])
  const [selectedId, setSelectedId] = useState<string | null>(null)
  const [snapshot, setSnapshot] = useState<SimSnapshot | null>(null)
  const [orders, setOrders] = useState<SimOrder[]>([])
  const [loading, setLoading] = useState(true)
  const [failed, setFailed] = useState<string | null>(null)
  const [creating, setCreating] = useState(false)
  const [actionError, setActionError] = useState<string | null>(null)
  const [confirmReset, setConfirmReset] = useState(false)
  const [busy, setBusy] = useState(false)

  const loadSessions = useCallback(async () => {
    try {
      const s = await api.simSessions()
      setSessions(s)
      setFailed(null)
      return s
    } catch (e) {
      setFailed(e instanceof ApiError && e.status === 403
        ? 'Your role does not permit viewing practice sessions (backtest:view).'
        : 'The simulation endpoints could not be reached.')
      return []
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    if (!canView) { setLoading(false); return }
    void loadSessions().then((s) => {
      // Resume the most recent active session rather than opening on a blank
      // picker — a practice tool should drop the trader back into practice.
      setSelectedId((cur) => cur ?? s[0]?.id ?? null)
    })
  }, [canView, loadSessions])

  // Snapshot + orders poll while a session is on screen. The snapshot endpoint
  // also marks stale quotes and re-marks positions, so polling IS the refresh.
  const refreshSelected = useCallback(async (id: string) => {
    const [snap, ords] = await Promise.allSettled([
      api.simSnapshot(id), api.simOrders(id),
    ])
    if (snap.status === 'fulfilled') setSnapshot(snap.value)
    if (ords.status === 'fulfilled') setOrders(ords.value)
  }, [])

  useEffect(() => {
    if (!selectedId || !canView) { setSnapshot(null); setOrders([]); return }
    let cancelled = false
    let timer: number | undefined
    const tick = async () => {
      if (cancelled) return
      await refreshSelected(selectedId)
      if (!cancelled) timer = window.setTimeout(tick, SNAPSHOT_POLL_MS)
    }
    void tick()
    return () => { cancelled = true; if (timer != null) window.clearTimeout(timer) }
  }, [selectedId, canView, refreshSelected])

  useEffect(() => {
    setConfirmReset(false)
    setActionError(null)
  }, [selectedId])

  async function doReset() {
    if (!selectedId) return
    setBusy(true)
    setActionError(null)
    try {
      await api.simReset(selectedId)
      setConfirmReset(false)
      await refreshSelected(selectedId)
      await loadSessions()
    } catch (e) {
      setActionError(e instanceof ApiError ? (e.detail ?? e.message) : String(e))
    } finally {
      setBusy(false)
    }
  }

  async function doClose() {
    if (!selectedId) return
    setBusy(true)
    setActionError(null)
    try {
      await api.simClose(selectedId)
      setSelectedId(null)
      setSnapshot(null)
      setOrders([])
      await loadSessions()
    } catch (e) {
      setActionError(e instanceof ApiError ? (e.detail ?? e.message) : String(e))
    } finally {
      setBusy(false)
    }
  }

  const session = snapshot?.session ?? sessions.find((s) => s.id === selectedId) ?? null

  if (loading) return <Panel title="Simulator"><Empty>Loading…</Empty></Panel>

  return (
    <Panel
      title="Simulator"
      actions={<PracticeBadge />}
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--sp-4)' }}>
        {!canView ? (
          <Banner tone="warn">
            Your role does not permit viewing practice sessions (backtest:view).
          </Banner>
        ) : failed ? (
          <Empty>{failed}</Empty>
        ) : (
          <>
            <div style={helpBox}>
              Everything on this screen is <b>practice</b>. Fills are simulated
              with the session's commission and slippage settings, nothing
              reaches a venue, and results are never merged with backtest,
              paper or live figures.
            </div>

            {/* --- session picker / creator --- */}
            <div style={{
              display: 'flex', gap: 'var(--sp-2)', alignItems: 'center', flexWrap: 'wrap',
            }}>
              {sessions.length > 0 && (
                <select
                  value={selectedId ?? ''}
                  onChange={(e) => setSelectedId(e.target.value || null)}
                  style={{ ...inputStyle, width: 'auto', minWidth: 220 }}
                  aria-label="Practice session"
                >
                  <option value="">— select a session —</option>
                  {sessions.map((s) => (
                    <option key={s.id} value={s.id}>
                      {s.name} · {s.data_source} · {fmtMoney(s.starting_cash, 'INR', true)}
                    </option>
                  ))}
                </select>
              )}
              {canAct && (
                <button onClick={() => setCreating((v) => !v)} style={{
                  padding: '5px var(--sp-3)',
                  background: creating ? 'var(--accent-bg)' : 'transparent',
                  border: `1px solid ${creating ? 'var(--accent)' : 'var(--border)'}`,
                  color: creating ? 'var(--accent)' : 'var(--text-secondary)',
                  borderRadius: 'var(--radius-sm)', fontSize: 'var(--fs-xs)',
                  cursor: 'pointer', fontFamily: 'inherit', fontWeight: 700,
                }}>
                  {creating ? 'Close' : '+ New session'}
                </button>
              )}
            </div>

            {creating && canAct && (
              <CreateSessionForm onCreated={async (s) => {
                setCreating(false)
                await loadSessions()
                setSelectedId(s.id)
              }} />
            )}

            {sessions.length === 0 && !creating && (
              <Empty>
                {canAct
                  ? 'No practice sessions yet. Create one to start trading virtual capital.'
                  : 'No practice sessions exist, and your role cannot create one (backtest:run).'}
              </Empty>
            )}

            {session && snapshot && (
              <>
                {/* --- header strip: equity, buying power, day loss --- */}
                <HeaderStrip snapshot={snapshot} />

                {actionError && <Banner tone="danger">{actionError}</Banner>}

                {/* --- reset / close controls --- */}
                {canAct && session.status === 'ACTIVE' && (
                  <div style={{
                    display: 'flex', gap: 'var(--sp-2)', alignItems: 'center',
                    flexWrap: 'wrap',
                  }}>
                    {confirmReset ? (
                      <>
                        <Banner tone="warn">
                          Reset wipes this session's orders, positions and P&L
                          and restores {fmtMoney(session.starting_cash)}.
                        </Banner>
                        <button disabled={busy} onClick={doReset} style={resetBtn}>
                          {busy ? 'Resetting…' : 'Confirm reset'}
                        </button>
                        <button disabled={busy} onClick={() => setConfirmReset(false)}
                                style={secondaryBtn}>
                          Keep session
                        </button>
                      </>
                    ) : (
                      <>
                        <button onClick={() => setConfirmReset(true)} style={resetBtn}>
                          RESET SESSION
                        </button>
                        <span className="num" style={{
                          fontSize: 'var(--fs-2xs)',
                          color: session.reset_count > 0 ? 'var(--warn)' : 'var(--text-muted)',
                        }}>
                          {/* The counter is the point: resetting away a loss is
                              a habit worth noticing. */}
                          reset {session.reset_count} time{session.reset_count === 1 ? '' : 's'}
                        </span>
                        <button disabled={busy} onClick={doClose} style={{
                          ...secondaryBtn, marginLeft: 'auto', color: 'var(--loss)',
                        }}>
                          Close session
                        </button>
                      </>
                    )}
                  </div>
                )}

                {session.status !== 'ACTIVE' && (
                  <Banner tone="warn">
                    This session is {session.status.toLowerCase()}. It can be
                    reviewed but not traded.
                  </Banner>
                )}

                <div style={{
                  display: 'grid',
                  gridTemplateColumns: 'minmax(230px, 280px) 1fr',
                  gap: 'var(--sp-4)', alignItems: 'start',
                }}>
                  {/* --- ticket --- */}
                  <SimTicket
                    session={session}
                    canAct={canAct && session.status === 'ACTIVE'}
                    onPlaced={() => void refreshSelected(session.id)}
                  />

                  {/* --- positions + orders --- */}
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--sp-4)', minWidth: 0 }}>
                    <PositionsTable snapshot={snapshot} />
                    <OrdersTable
                      orders={orders}
                      canAct={canAct && session.status === 'ACTIVE'}
                      onCancel={async (oid) => {
                        setActionError(null)
                        try {
                          await api.simCancelOrder(session.id, oid)
                          await refreshSelected(session.id)
                        } catch (e) {
                          setActionError(e instanceof ApiError
                            ? (e.detail ?? e.message) : String(e))
                        }
                      }}
                    />
                  </div>
                </div>

                <div style={{ fontSize: 9, color: 'var(--text-muted)', lineHeight: 1.5 }}>
                  Session assumptions: commission {fmtPrice(session.commission_per_share, 3)}/share,
                  slippage {fmtPrice(session.slippage_bps, 1)} bp,
                  buying power ×{fmtPrice(session.buying_power_mult, 0)}
                  {session.max_daily_loss != null &&
                    `, max daily loss ${fmtMoney(session.max_daily_loss)}`}
                  {session.max_position_qty != null &&
                    `, max position ${fmtPrice(session.max_position_qty, 0)} shares`}.
                  Data source {session.data_source}
                  {isSimulatedSource(session.data_source)
                    ? ' — prices are INVENTED by the simulator, not observed in any market.'
                    : ' — real market prices, simulated fills.'}
                </div>
              </>
            )}
          </>
        )}
      </div>
    </Panel>
  )
}

// ===========================================================================
// Header strip
// ===========================================================================

function HeaderStrip({ snapshot }: { snapshot: SimSnapshot }) {
  const s = snapshot.session
  const totalPnl = Number(snapshot.total_pnl)
  const unreal = Number(snapshot.unrealized_pnl)
  const maxLoss = s.max_daily_loss != null ? Number(s.max_daily_loss) : null
  const lossUsed = Math.max(0, -totalPnl)

  return (
    <div style={{
      display: 'flex', flexDirection: 'column', gap: 'var(--sp-2)',
      padding: 'var(--sp-2) var(--sp-3)',
      background: 'var(--bg-input)', border: '1px solid var(--border)',
      borderRadius: 'var(--radius-sm)',
    }}>
      <div style={{ display: 'flex', gap: 'var(--sp-5)', flexWrap: 'wrap' }}>
        <Stat label="Equity" value={fmtMoney(snapshot.equity)} />
        <Stat label="Buying power" value={fmtMoney(snapshot.buying_power)} />
        <Stat label="Cash" value={fmtMoney(s.cash)} />
        <Stat label="Session P&L" value={signedMoney(totalPnl)}
              tone={totalPnl > 0 ? 'profit' : totalPnl < 0 ? 'loss' : undefined} />
        <Stat label="Unrealised" value={signedMoney(unreal)}
              tone={unreal > 0 ? 'profit' : unreal < 0 ? 'loss' : undefined} />
        <Stat label="Commission paid" value={fmtMoney(s.commission_paid)} />
        <Stat label="Orders" value={`${snapshot.open_orders} open · ${snapshot.filled_orders} filled`} />
      </div>

      {/* The discipline rail, drawn as a meter rather than buried in settings:
          how much of today's loss allowance is already spent. */}
      {maxLoss != null && maxLoss > 0 && (
        <Meter
          value={lossUsed}
          max={maxLoss}
          label="Daily loss limit used"
          format={(v) => fmtMoney(v, 'INR', true)}
        />
      )}
    </div>
  )
}

function Stat({ label, value, tone }: {
  label: string; value: string; tone?: 'profit' | 'loss'
}) {
  const color = tone === 'profit' ? 'var(--profit)'
              : tone === 'loss' ? 'var(--loss)' : 'var(--text-primary)'
  return (
    <span style={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
      <span style={{
        fontSize: 'var(--fs-2xs)', textTransform: 'uppercase',
        letterSpacing: '0.06em', color: 'var(--text-muted)',
      }}>
        {label}
      </span>
      <span className="num" style={{ color, fontWeight: 700, fontSize: 'var(--fs-md)' }}>
        {value}
      </span>
    </span>
  )
}

const signedMoney = (n: number) =>
  `${n > 0 ? '+' : ''}${fmtMoney(n)}`

// ===========================================================================
// Session creation
// ===========================================================================

const DATA_SOURCES: { id: SimDataSource; label: string; note: string }[] = [
  { id: 'RANDOM_WALK', label: 'Random walk',
    note: 'invented prices — practice mechanics, not market reading' },
  { id: 'UPSTOX_NSE', label: 'Upstox NSE',
    note: 'real NSE prices, simulated fills' },
  { id: 'ALPACA', label: 'Alpaca (US)',
    note: 'real US prices, simulated fills' },
  { id: 'REPLAY', label: 'Replay',
    note: 'recorded history played back' },
]

function CreateSessionForm({ onCreated }: { onCreated: (s: SimSession) => void }) {
  const [name, setName] = useState('')
  const [source, setSource] = useState<SimDataSource>('RANDOM_WALK')
  const [mode, setMode] = useState<SimMode>('REALTIME')
  const [cash, setCash] = useState('100000')
  const [bpMult, setBpMult] = useState('4')
  const [commission, setCommission] = useState('0.005')
  const [slippage, setSlippage] = useState('2')
  const [maxLoss, setMaxLoss] = useState('')
  const [maxQty, setMaxQty] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [submitting, setSubmitting] = useState(false)

  async function submit() {
    setSubmitting(true)
    setError(null)
    try {
      const s = await api.simCreateSession({
        name: name.trim() || `Practice ${new Date().toLocaleDateString('en-GB')}`,
        mode,
        data_source: source,
        starting_cash: cash,
        buying_power_mult: bpMult,
        commission_per_share: commission,
        slippage_bps: slippage,
        // Omitted rails are DISABLED, not zero — the API treats null as "no
        // rail", and sending 0 would be rejected.
        max_daily_loss: maxLoss.trim() === '' ? null : maxLoss,
        max_position_qty: maxQty.trim() === '' ? null : maxQty,
      })
      onCreated(s)
    } catch (e) {
      setError(e instanceof ApiError ? (e.detail ?? e.message) : String(e))
    } finally {
      setSubmitting(false)
    }
  }

  const srcNote = DATA_SOURCES.find((d) => d.id === source)?.note

  return (
    <div style={{
      padding: 'var(--sp-3)', background: 'var(--bg-input)',
      border: '1px solid var(--border)', borderRadius: 'var(--radius-sm)',
      display: 'flex', flexDirection: 'column', gap: 'var(--sp-3)', maxWidth: 640,
    }}>
      <div style={{
        display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))',
        gap: 'var(--sp-2)',
      }}>
        <Field label="Session name">
          <input value={name} onChange={(e) => setName(e.target.value)}
                 placeholder="e.g. Morning practice" style={inputStyle} />
        </Field>
        <Field label="Data source">
          <select value={source} onChange={(e) => setSource(e.target.value as SimDataSource)}
                  style={inputStyle}>
            {DATA_SOURCES.map((d) => <option key={d.id} value={d.id}>{d.label}</option>)}
          </select>
        </Field>
        <Field label="Mode">
          <select value={mode} onChange={(e) => setMode(e.target.value as SimMode)}
                  style={inputStyle}>
            <option value="REALTIME">REALTIME</option>
            <option value="REPLAY">REPLAY</option>
          </select>
        </Field>
        <Field label="Starting cash">
          <input className="num" inputMode="decimal" value={cash}
                 onChange={(e) => setCash(e.target.value)} style={inputStyle} />
        </Field>
        <Field label="Buying power ×">
          <input className="num" inputMode="decimal" value={bpMult}
                 onChange={(e) => setBpMult(e.target.value)} style={inputStyle} />
        </Field>
        <Field label="Commission / share">
          <input className="num" inputMode="decimal" value={commission}
                 onChange={(e) => setCommission(e.target.value)} style={inputStyle} />
        </Field>
        <Field label="Slippage (bp)">
          <input className="num" inputMode="decimal" value={slippage}
                 onChange={(e) => setSlippage(e.target.value)} style={inputStyle} />
        </Field>
        <Field label="Max daily loss (blank = off)">
          <input className="num" inputMode="decimal" value={maxLoss}
                 onChange={(e) => setMaxLoss(e.target.value)}
                 placeholder="disabled" style={inputStyle} />
        </Field>
        <Field label="Max position qty (blank = off)">
          <input className="num" inputMode="decimal" value={maxQty}
                 onChange={(e) => setMaxQty(e.target.value)}
                 placeholder="disabled" style={inputStyle} />
        </Field>
      </div>

      {srcNote && (
        <div style={{ fontSize: 9, color: 'var(--text-muted)' }}>{srcNote}</div>
      )}
      {error && <Banner tone="danger">{error}</Banner>}

      <button disabled={submitting || !(Number(cash) > 0)} onClick={submit}
              style={primaryBtn(submitting || !(Number(cash) > 0))}>
        {submitting ? 'Creating…' : 'Start practice session'}
      </button>
    </div>
  )
}

// ===========================================================================
// Ticket
// ===========================================================================

function SimTicket({ session, canAct, onPlaced }: {
  session: SimSession
  canAct: boolean
  onPlaced: () => void
}) {
  const [symbol, setSymbol] = useState('')
  const [side, setSide] = useState<OrderSide>('BUY')
  const [orderType, setOrderType] = useState<SimOrderType>('MARKET')
  const [quantity, setQuantity] = useState('')
  const [limitPrice, setLimitPrice] = useState('')
  const [stopPrice, setStopPrice] = useState('')
  const [quote, setQuote] = useState<SimQuote | null>(null)
  const [quoteError, setQuoteError] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [placed, setPlaced] = useState<string | null>(null)
  const [submitting, setSubmitting] = useState(false)
  const placedTimer = useRef<number | null>(null)

  const needsLimit = orderType === 'LIMIT' || orderType === 'STOP_LIMIT'
  const needsStop = orderType === 'STOP' || orderType === 'STOP_LIMIT'

  // Quote for the typed symbol, from the SESSION's data source, debounced and
  // refreshed on the same cadence as the snapshot while the symbol holds.
  useEffect(() => {
    const sym = symbol.trim().toUpperCase()
    if (!sym) { setQuote(null); setQuoteError(null); return }
    let cancelled = false
    let timer: number | undefined
    const fetchQuote = async () => {
      try {
        const q = await api.simQuote(sym, session.data_source)
        if (!cancelled) { setQuote(q); setQuoteError(null) }
      } catch (e) {
        if (!cancelled) {
          setQuote(null)
          setQuoteError(e instanceof ApiError ? (e.detail ?? e.message) : 'no quote')
        }
      }
      if (!cancelled) timer = window.setTimeout(fetchQuote, SNAPSHOT_POLL_MS)
    }
    timer = window.setTimeout(fetchQuote, 350)
    return () => { cancelled = true; if (timer != null) window.clearTimeout(timer) }
  }, [symbol, session.data_source])

  useEffect(() => () => {
    if (placedTimer.current != null) window.clearTimeout(placedTimer.current)
  }, [])

  async function submit() {
    setSubmitting(true)
    setError(null)
    try {
      const o = await api.simPlaceOrder(session.id, {
        symbol: symbol.trim().toUpperCase(),
        side,
        order_type: orderType,
        quantity,
        limit_price: needsLimit ? limitPrice : null,
        stop_price: needsStop ? stopPrice : null,
      })
      if (o.status === 'REJECTED') {
        // A rejection is an ANSWER, not an error banner buried in a list.
        setError(`Rejected: ${o.reject_reason ?? 'no reason recorded'}`)
      } else {
        setPlaced(`${o.side} ${fmtPrice(o.quantity, 0)} ${o.symbol} — ${o.status}`)
        setQuantity('')
        if (placedTimer.current != null) window.clearTimeout(placedTimer.current)
        placedTimer.current = window.setTimeout(() => setPlaced(null), 6000)
      }
      onPlaced()
    } catch (e) {
      setError(e instanceof ApiError ? (e.detail ?? e.message) : String(e))
    } finally {
      setSubmitting(false)
    }
  }

  const blocked = !canAct || !symbol.trim() || !(Number(quantity) > 0) ||
                  (needsLimit && !(Number(limitPrice) > 0)) ||
                  (needsStop && !(Number(stopPrice) > 0))
  const accent = side === 'BUY' ? 'var(--buy)' : 'var(--sell)'

  return (
    <div style={{
      padding: 'var(--sp-3)', background: 'var(--bg-input)',
      border: '1px solid var(--border)', borderRadius: 'var(--radius-sm)',
      display: 'flex', flexDirection: 'column', gap: 'var(--sp-2)',
    }}>
      <div style={{
        fontSize: 'var(--fs-2xs)', fontWeight: 700, letterSpacing: '0.08em',
        textTransform: 'uppercase', color: 'var(--text-muted)',
      }}>
        Practice ticket
      </div>

      {!canAct && (
        <div style={{ fontSize: 9, color: 'var(--warn)' }}>
          Orders need backtest:run and an active session.
        </div>
      )}

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 'var(--sp-2)' }}>
        {(['BUY', 'SELL'] as const).map((s) => (
          <button key={s} onClick={() => setSide(s)} aria-pressed={side === s}
                  disabled={!canAct}
                  style={{
                    padding: 'var(--sp-2)',
                    border: `1px solid ${side === s
                      ? (s === 'BUY' ? 'var(--buy-border)' : 'var(--sell-border)')
                      : 'var(--border)'}`,
                    background: side === s
                      ? (s === 'BUY' ? 'var(--buy-bg)' : 'var(--sell-bg)')
                      : 'var(--bg-panel)',
                    color: side === s
                      ? (s === 'BUY' ? 'var(--buy)' : 'var(--sell)')
                      : 'var(--text-secondary)',
                    borderRadius: 'var(--radius-sm)', fontWeight: 700,
                    fontSize: 'var(--fs-sm)', cursor: canAct ? 'pointer' : 'not-allowed',
                    fontFamily: 'inherit',
                  }}>
            {s}
          </button>
        ))}
      </div>

      <Field label="Symbol">
        <input value={symbol}
               onChange={(e) => setSymbol(e.target.value.toUpperCase())}
               placeholder="e.g. RELIANCE" disabled={!canAct}
               style={{ ...inputStyle, textTransform: 'uppercase' }} />
      </Field>

      {/* Quote with its provenance. An invented price says so. */}
      {symbol.trim() && (
        <div style={{
          fontSize: 'var(--fs-2xs)', display: 'flex',
          justifyContent: 'space-between', gap: 'var(--sp-2)',
          color: 'var(--text-muted)',
        }}>
          {quote ? (
            <>
              <span className="num">
                {fmtPrice(quote.bid)} / {fmtPrice(quote.ask)} · last {fmtPrice(quote.last)}
              </span>
              <span style={{
                color: isSimulatedSource(quote.source) ? 'var(--warn)' : 'var(--text-muted)',
              }}>
                {isSimulatedSource(quote.source) ? `${quote.source} · invented` : quote.source}
              </span>
            </>
          ) : (
            <span>{quoteError ?? 'fetching quote…'}</span>
          )}
        </div>
      )}

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 'var(--sp-2)' }}>
        <Field label="Type">
          <select value={orderType}
                  onChange={(e) => setOrderType(e.target.value as SimOrderType)}
                  disabled={!canAct} style={inputStyle}>
            {(['MARKET', 'LIMIT', 'STOP', 'STOP_LIMIT'] as const).map((t) =>
              <option key={t} value={t}>{t.replace('_', ' ')}</option>)}
          </select>
        </Field>
        <Field label="Quantity">
          <input className="num" inputMode="decimal" value={quantity}
                 onChange={(e) => setQuantity(e.target.value)}
                 placeholder="0" disabled={!canAct} style={inputStyle} />
        </Field>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 'var(--sp-2)' }}>
        <Field label={needsLimit ? 'Limit price' : 'Limit (n/a)'}>
          <input className="num" inputMode="decimal" value={limitPrice}
                 onChange={(e) => setLimitPrice(e.target.value)}
                 disabled={!canAct || !needsLimit}
                 placeholder={needsLimit ? '0.00' : '—'}
                 style={{ ...inputStyle, opacity: needsLimit ? 1 : 0.4 }} />
        </Field>
        <Field label={needsStop ? 'Stop price' : 'Stop (n/a)'}>
          <input className="num" inputMode="decimal" value={stopPrice}
                 onChange={(e) => setStopPrice(e.target.value)}
                 disabled={!canAct || !needsStop}
                 placeholder={needsStop ? '0.00' : '—'}
                 style={{ ...inputStyle, opacity: needsStop ? 1 : 0.4 }} />
        </Field>
      </div>

      {error && <Banner tone="danger">{error}</Banner>}
      {placed && <Banner tone="ok">{placed}</Banner>}

      <button
        disabled={blocked || submitting}
        onClick={submit}
        style={{
          padding: 'var(--sp-2)', border: 'none', borderRadius: 'var(--radius-sm)',
          fontWeight: 700, fontSize: 'var(--fs-sm)', fontFamily: 'inherit',
          background: blocked || submitting ? 'var(--bg-hover)' : accent,
          color: blocked || submitting ? 'var(--text-muted)' : '#fff',
          cursor: blocked || submitting ? 'not-allowed' : 'pointer',
        }}
      >
        {submitting ? 'Placing…' : `${side} ${quantity || ''} (simulated)`}
      </button>
    </div>
  )
}

// ===========================================================================
// Tables
// ===========================================================================

function PositionsTable({ snapshot }: { snapshot: SimSnapshot }) {
  if (snapshot.positions.length === 0) {
    return (
      <div>
        <SubHeading>Open positions</SubHeading>
        <Empty>No open positions.</Empty>
      </div>
    )
  }
  return (
    <div>
      <SubHeading>Open positions · {snapshot.positions.length}</SubHeading>
      <div className="scroll-x">
        <table className="qx">
          <thead>
            <tr>
              <th>Symbol</th><th className="r">Qty</th><th className="r">Avg</th>
              <th className="r">Last</th><th className="r">Mkt value</th>
              <th className="r">Unreal P&L</th><th className="r">Real P&L</th>
            </tr>
          </thead>
          <tbody>
            {snapshot.positions.map((p) => {
              const unreal = p.unrealized_pnl != null ? Number(p.unrealized_pnl) : null
              const real = Number(p.realized_pnl)
              const qty = Number(p.quantity)
              return (
                <tr key={p.symbol}>
                  <td style={{ fontWeight: 600 }}>{p.symbol}</td>
                  <td className="num r" style={{
                    color: qty > 0 ? 'var(--buy)' : qty < 0 ? 'var(--sell)' : undefined,
                  }}>
                    {qty > 0 ? '+' : ''}{fmtPrice(qty, 0)}
                  </td>
                  <td className="num r">{fmtPrice(p.avg_price)}</td>
                  <td className="num r">{fmtPrice(p.last_price)}</td>
                  <td className="num r">{p.market_value != null ? fmtPrice(p.market_value) : '—'}</td>
                  <td className="num r" style={{
                    color: unreal == null ? 'var(--text-muted)'
                         : unreal >= 0 ? 'var(--profit)' : 'var(--loss)',
                  }}>
                    {unreal == null ? '—' : `${unreal >= 0 ? '+' : ''}${fmtPrice(unreal)}`}
                  </td>
                  <td className="num r" style={{
                    color: real >= 0 ? 'var(--profit)' : 'var(--loss)',
                  }}>
                    {real >= 0 ? '+' : ''}{fmtPrice(real)}
                  </td>
                </tr>
              )
            })}
          </tbody>
        </table>
      </div>
    </div>
  )
}

function OrdersTable({ orders, canAct, onCancel }: {
  orders: SimOrder[]
  canAct: boolean
  onCancel: (id: string) => void
}) {
  const sorted = useMemo(
    () => orders.slice().sort((a, b) => b.created_at.localeCompare(a.created_at)),
    [orders])

  return (
    <div>
      <SubHeading>Orders · {orders.length}</SubHeading>
      {orders.length === 0 ? (
        <Empty>No orders in this session.</Empty>
      ) : (
        <div className="scroll-x" style={{ maxHeight: 260, overflowY: 'auto' }}>
          <table className="qx">
            <thead>
              <tr>
                <th>Symbol</th><th>Side</th><th>Type</th>
                <th className="r">Qty</th><th className="r">Limit</th>
                <th className="r">Stop</th><th>Status</th>
                <th className="r">Fill</th><th className="r">Comm.</th>
                <th>Placed</th><th></th>
              </tr>
            </thead>
            <tbody>
              {sorted.map((o) => (
                <tr key={o.id}>
                  <td style={{ fontWeight: 600 }}>{o.symbol}</td>
                  <td style={{ color: o.side === 'BUY' ? 'var(--buy)' : 'var(--sell)' }}>
                    {o.side}
                  </td>
                  <td style={{ color: 'var(--text-muted)' }}>
                    {o.order_type.replace('_', ' ')}
                  </td>
                  <td className="num r">{fmtPrice(o.quantity, 0)}</td>
                  <td className="num r">{o.limit_price != null ? fmtPrice(o.limit_price) : '—'}</td>
                  <td className="num r">{o.stop_price != null ? fmtPrice(o.stop_price) : '—'}</td>
                  <td style={{ color: simOrderStatusColor(o.status) }}
                      title={o.reject_reason ?? undefined}>
                    {o.status}
                    {o.reject_reason ? ' ⓘ' : ''}
                  </td>
                  <td className="num r">
                    {Number(o.filled_quantity) > 0
                      ? `${fmtPrice(o.filled_quantity, 0)} @ ${fmtPrice(o.avg_fill_price)}`
                      : '—'}
                  </td>
                  <td className="num r" style={{ color: 'var(--text-muted)' }}>
                    {fmtPrice(o.commission)}
                  </td>
                  <td className="num" style={{ color: 'var(--text-muted)' }}>
                    {fmtDateTime(o.created_at)}
                  </td>
                  <td>
                    {/* Only a NEW (working) order can be pulled. */}
                    {canAct && o.status === 'NEW' && (
                      <button onClick={() => onCancel(o.id)} style={{
                        background: 'none', border: 'none', color: 'var(--loss)',
                        cursor: 'pointer', fontSize: 'var(--fs-2xs)',
                        fontFamily: 'inherit', padding: 0,
                      }}>
                        cancel
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  )
}

function simOrderStatusColor(status: string): string {
  switch (status?.toUpperCase()) {
    case 'FILLED': return 'var(--ok)'
    case 'NEW': return 'var(--warn)'
    case 'REJECTED': return 'var(--danger)'
    case 'CANCELLED': return 'var(--text-muted)'
    default: return 'var(--text-secondary)'
  }
}

// ===========================================================================
// Shared bits (house style: private per panel)
// ===========================================================================

function PracticeBadge() {
  return (
    <span
      title="A practice session with virtual capital. Fills are simulated; nothing reaches a venue."
      style={{
        display: 'inline-flex', alignItems: 'center', gap: 'var(--sp-1)',
        padding: '2px var(--sp-2)', borderRadius: 'var(--radius-sm)',
        border: '1px solid var(--mode-paper)',
        background: 'color-mix(in srgb, var(--mode-paper) 14%, transparent)',
        color: 'var(--mode-paper)', fontSize: 'var(--fs-2xs)', fontWeight: 700,
        letterSpacing: '0.06em', textTransform: 'uppercase', whiteSpace: 'nowrap',
      }}
    >
      <span aria-hidden style={{
        width: 6, height: 6, borderRadius: '50%', background: 'var(--mode-paper)',
      }} />
      SIMULATED · practice only
    </span>
  )
}

function SubHeading({ children }: { children: React.ReactNode }) {
  return (
    <div style={{
      fontSize: 'var(--fs-2xs)', fontWeight: 700, letterSpacing: '0.06em',
      textTransform: 'uppercase', color: 'var(--text-muted)',
      marginBottom: 'var(--sp-1)',
    }}>
      {children}
    </div>
  )
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label style={{ display: 'flex', flexDirection: 'column', gap: 'var(--sp-1)', minWidth: 0 }}>
      <span style={{
        fontSize: 'var(--fs-2xs)', textTransform: 'uppercase',
        letterSpacing: '0.06em', color: 'var(--text-muted)',
      }}>
        {label}
      </span>
      {children}
    </label>
  )
}

function Banner({ tone, children }: {
  tone: 'danger' | 'warn' | 'ok'; children: React.ReactNode
}) {
  const color = tone === 'danger' ? 'var(--danger)'
              : tone === 'warn' ? 'var(--warn)' : 'var(--ok)'
  const bg = tone === 'danger' ? 'var(--danger-bg)'
           : tone === 'warn' ? 'var(--warn-bg)' : 'var(--ok-bg)'
  return (
    <div style={{
      padding: 'var(--sp-2) var(--sp-3)', background: bg,
      border: `1px solid ${color}`, borderRadius: 'var(--radius-sm)',
      color, fontSize: 'var(--fs-xs)', lineHeight: 1.5,
    }}>
      {children}
    </div>
  )
}

const inputStyle: React.CSSProperties = {
  width: '100%', padding: 'var(--sp-2)',
  background: 'var(--bg-input)', border: '1px solid var(--border)',
  borderRadius: 'var(--radius-sm)', color: 'var(--text-primary)',
  fontSize: 'var(--fs-sm)', fontFamily: 'inherit',
}

const helpBox: React.CSSProperties = {
  padding: 'var(--sp-2)', background: 'var(--bg-input)',
  border: '1px solid var(--border)', borderRadius: 'var(--radius-sm)',
  fontSize: 9, color: 'var(--text-muted)', lineHeight: 1.5,
}

const primaryBtn = (disabled: boolean): React.CSSProperties => ({
  padding: 'var(--sp-2) var(--sp-4)', border: 'none',
  borderRadius: 'var(--radius-sm)', fontWeight: 700, fontSize: 'var(--fs-sm)',
  letterSpacing: '0.03em', fontFamily: 'inherit',
  background: disabled ? 'var(--bg-hover)' : 'var(--accent)',
  color: disabled ? 'var(--text-muted)' : '#fff',
  cursor: disabled ? 'not-allowed' : 'pointer',
})

const secondaryBtn: React.CSSProperties = {
  padding: '5px var(--sp-3)', border: '1px solid var(--border)',
  borderRadius: 'var(--radius-sm)', background: 'var(--bg-input)',
  color: 'var(--text-secondary)', cursor: 'pointer',
  fontSize: 'var(--fs-xs)', fontFamily: 'inherit',
}

const resetBtn: React.CSSProperties = {
  padding: '5px var(--sp-4)', border: '1px solid var(--warn)',
  borderRadius: 'var(--radius-sm)', background: 'var(--warn-bg)',
  color: 'var(--warn)', cursor: 'pointer', fontWeight: 800,
  fontSize: 'var(--fs-xs)', letterSpacing: '0.05em', fontFamily: 'inherit',
}
